import { useEffect, useRef } from "react";

import { Box, Button, Divider, Group, ScrollArea, Stack } from "@mantine/core";
import { UseFormReturnType } from "@mantine/form";

import { IconRefresh } from "@tabler/icons-react";

import { TextWrapper } from "@/components/TextWrapper";
import { useUndoableRemove } from "@/encounters/hooks/useUndoableRemove";
import { ProceduresPickerFormType } from "@/encounters/zod-schemas/procedures";
import { ServiceDateKey } from "@/shared/procedure-code-effective-dates";

import SelectedDiagnosisRow from "./SelectedDiagnosisRow";
import SelectedProcedureRow from "./SelectedProcedureRow";

function ProceduresPickerRightPanel({
  form,
  conditionsLocked = false,
  proceduresLocked = false,
  serviceDate,
}: {
  form: UseFormReturnType<ProceduresPickerFormType>;
  /** The picker opened on a single diagnosis, so the diagnosis selection cannot be changed. */
  conditionsLocked?: boolean;
  /** The picker opened on a single procedure code, so the procedure selection cannot be changed. */
  proceduresLocked?: boolean;
  /** The encounter's service date (`yyyy-MM-dd`); codes not billable on it show a warning. */
  serviceDate?: ServiceDateKey;
}) {
  const conditions = form.values.conditions;
  const procedures = form.values.procedures;
  const { pendingUndo, removeWithUndo, undo, dismissUndo } = useUndoableRemove();
  const undoButtonRef = useRef<HTMLButtonElement>(null);
  const removeButtonsByRowKey = useRef(new Map<string, HTMLButtonElement>());
  const rowKeyToFocusAfterUndo = useRef<string | null>(null);

  // Moves focus to Undo when a removal starts, since the removed row's remove button unmounts.
  // Only a new removal triggers it, so picking or editing codes while Undo is pending keeps focus.
  useEffect(() => {
    if (pendingUndo) {
      undoButtonRef.current?.focus();
    }
  }, [pendingUndo]);

  // Undo unmounts with the bar, so once a restore lands, focus moves back to that code's row. It
  // runs after the lists update, which is when the restored row's remove button exists.
  useEffect(() => {
    const rowKey = rowKeyToFocusAfterUndo.current;
    if (!rowKey) return;
    rowKeyToFocusAfterUndo.current = null;
    removeButtonsByRowKey.current.get(rowKey)?.focus();
  }, [pendingUndo, conditions, procedures]);

  const registerRemoveButton = (rowKey: string) => (button: HTMLButtonElement | null) => {
    if (button) {
      removeButtonsByRowKey.current.set(rowKey, button);
    } else {
      removeButtonsByRowKey.current.delete(rowKey);
    }
  };

  const handleRemove = (listKey: SelectedCodeListKey, index: number) => {
    const list = form.getValues()[listKey];
    const removed = list[index];
    const codesAfterRemoved = list.slice(index + 1).map((item) => item.code);
    removeWithUndo({
      message: `${REMOVED_MESSAGE_PREFIX[listKey]}: ${removed.code}`,
      remove: () => form.removeListItem(listKey, index),
      restore: () => {
        // Focus lands on this code's row either way: the restored one, or the one picked again.
        rowKeyToFocusAfterUndo.current = rowKeyOf(listKey, removed.code);
        // Skip when the same code was picked again meanwhile, so the list holds each code once.
        const currentCodes = form.getValues()[listKey].map((item) => item.code);
        if (currentCodes.includes(removed.code)) return;
        // Other rows may have gone meanwhile, so the code goes back before the first row that
        // followed it and is still there, else at the end.
        const nextSurvivingCode = codesAfterRemoved.find((code) => currentCodes.includes(code));
        const restoreAt = nextSurvivingCode
          ? currentCodes.indexOf(nextSurvivingCode)
          : currentCodes.length;
        form.insertListItem(listKey, removed, restoreAt);
      },
    });
  };

  // Clears everything this session is allowed to change. Whichever side is locked stays put.
  const handleClearAll = () => {
    dismissUndo();
    form.setValues({
      conditions: conditionsLocked ? conditions : [],
      procedures: proceduresLocked ? procedures : [],
    });
    form.clearErrors();
  };

  return (
    <Stack
      p="md"
      flex={1}
      gap="md"
      bg="gray.0"
      style={{
        borderLeft: "1px solid var(--mantine-color-cream-4)",
        overflow: "hidden",
      }}
    >
      <Group justify="space-between">
        <TextWrapper fz="md" fw={600}>
          Selected ICD-10 & CPT Codes
        </TextWrapper>
        <Group
          gap="xs"
          onClick={handleClearAll}
          style={{ cursor: "pointer" }}
          data-testid="clear-all-button"
        >
          <TextWrapper fw={600} size="md" c="burgundy.9">
            Clear All
          </TextWrapper>
          <IconRefresh size={20} />
        </Group>
      </Group>

      <Stack gap={0}>
        <Divider />

        {/* The status region stays mounted so screen readers announce the message when it changes.
            The bar is keyed by removal, so a repeated message is a new node and is announced again. */}
        <Box role="status">
          {pendingUndo && (
            <Group
              mt="md"
              px="sm"
              py="xs"
              gap="sm"
              bg="white"
              wrap="nowrap"
              key={pendingUndo.id}
              justify="space-between"
              style={{ borderRadius: 8 }}
              data-testid="undo-remove-bar"
              bd="1px solid var(--mantine-color-cream-4)"
            >
              <TextWrapper size="md">{pendingUndo.message}</TextWrapper>
              <Button
                fz="md"
                size="xs"
                onClick={undo}
                variant="outline"
                ref={undoButtonRef}
                data-testid="undo-remove-button"
              >
                Undo
              </Button>
            </Group>
          )}
        </Box>
      </Stack>

      <ScrollArea flex={1} offsetScrollbars>
        <Stack gap="md">
          <Stack gap="xs">
            <TextWrapper fw={700} size="xs" c="gray.7" tt="uppercase" style={{ letterSpacing: 1 }}>
              Diagnoses
            </TextWrapper>
            {conditions.length === 0 ? (
              <TextWrapper size="md" c="black" variant="default">
                No diagnoses have been selected
              </TextWrapper>
            ) : (
              conditions.map((item, index) => (
                <SelectedDiagnosisRow
                  key={item.code}
                  code={item.code}
                  description={item.display}
                  isRemovable={!conditionsLocked}
                  onRemove={() => handleRemove("conditions", index)}
                  removeButtonRef={registerRemoveButton(rowKeyOf("conditions", item.code))}
                />
              ))
            )}
          </Stack>

          <Stack gap="xs">
            <TextWrapper fw={700} size="xs" c="gray.7" tt="uppercase" style={{ letterSpacing: 1 }}>
              Procedures
            </TextWrapper>
            {procedures.length === 0 ? (
              <TextWrapper size="md" c="black" variant="default">
                No procedures have been selected
              </TextWrapper>
            ) : (
              procedures.map((item, index) => (
                <SelectedProcedureRow
                  form={form}
                  index={index}
                  key={item.code}
                  code={item.code}
                  serviceDate={serviceDate}
                  description={item.display}
                  isRemovable={!proceduresLocked}
                  onRemove={() => handleRemove("procedures", index)}
                  removeButtonRef={registerRemoveButton(rowKeyOf("procedures", item.code))}
                />
              ))
            )}
          </Stack>
        </Stack>
      </ScrollArea>
    </Stack>
  );
}

export default ProceduresPickerRightPanel;

/** Identifies a selected-code row across renders: a code appears at most once per list. */
function rowKeyOf(listKey: SelectedCodeListKey, code: string): string {
  return `${listKey}:${code}`;
}

/** Start of the Undo bar's message after a selected code is removed, per list. */
const REMOVED_MESSAGE_PREFIX: Record<SelectedCodeListKey, string> = {
  conditions: "Diagnosis removed",
  procedures: "Procedure removed",
};

/** The two selected-code lists in the picker form. */
type SelectedCodeListKey = "conditions" | "procedures";
